<template>
  <div id="instagram-accounts-modal-app">
    <modal
      elem-id="modal_crud_instagram_accounts"
      size="small"
      :close-label="t('Fechar')"
      :save-label="t('Salvar')"
      :save-disabled-fn="() => !formRef?.canSave"
      :save-fn="handleSave"
      :title="t('Conta Instagram')"
      :show="isVisible"
      @close="closeModal"
      @dismiss="closeModal">

      <template v-if="isVisible">
        <div class="text-right" style="margin-bottom: 20px;">
          <Button
            v-if="shouldShowDelete"
            :label="t('Excluir registro')"
            icon="pi pi-trash"
            severity="danger"
            data-testid="instagram-accounts-delete-btn"
            @click="handleDelete" />
        </div>

        <instagram-accounts-form
          ref="formRef"
          :is-edit-mode="isEditMode"
          @save-success="handleSaveSuccess">
        </instagram-accounts-form>
      </template>
    </modal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { useInstagramAccounts } from '../../composables/useInstagramAccounts';
import { useConfirmAction } from '../../composables/useConfirmAction';
import { log } from '../../utils/DebugLogger';
import InstagramAccountsForm from './InstagramAccountsForm.vue';

type Id = number | string;

interface FormInstance {
  loadForEdit: (id: number) => Promise<void>;
  resetForm: () => void;
  save: () => Promise<void>;
  canSave: boolean;
}

interface ModalOpenPayload {
  id?: Id;
}

const { t } = useI18n();
const { confirmAction } = useConfirmAction();
const { removerInstagramAccount } = useInstagramAccounts();

const isVisible = ref<boolean>(false);
const isEditMode = ref<boolean>(false);
const formRef = ref<FormInstance | null>(null);
const currentId = ref<number>(0);

const shouldShowDelete = computed<boolean>(() => isEditMode.value && currentId.value > 0);

const openForEdit = async (instagramAccountId: number): Promise<void> => {
  isVisible.value = true;
  isEditMode.value = true;
  currentId.value = instagramAccountId;
  await nextTick();
  if (formRef.value) await formRef.value.loadForEdit(instagramAccountId);
};

const closeModal = (): void => {
  isVisible.value = false;
  isEditMode.value = false;
  currentId.value = 0;
  if (formRef.value) formRef.value.resetForm();
};

const handleSave = async (): Promise<void> => {
  if (formRef.value) await formRef.value.save();
};

const handleSaveSuccess = (): void => {
  closeModal();
  eventBus.emit('instagram-accounts.refresh');
  eventBus.emit('globalMessage.success', t('Registro salvo com sucesso!'));
};

const handleModalOpen = async (data?: ModalOpenPayload): Promise<void> => {
  log('InstagramAccountsModal', 'handleModalOpen', 'Event received', data);
  if (data?.id !== undefined) {
    const id = typeof data.id === 'string' ? parseInt(data.id, 10) : data.id;
    await openForEdit(id);
  }
};

const handleDelete = (): void => {
  confirmAction({
    message: t('Tem certeza que deseja remover esta conta Instagram?'),
    header: t('Confirmar Exclusão'),
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: t('Sim'),
    rejectLabel: t('Não'),
    onAccept: async (): Promise<void> => {
      try {
        await removerInstagramAccount(currentId.value);
        eventBus.emit('globalMessage.success', t('Conta removida com sucesso!'));
        closeModal();
        eventBus.emit('instagram-accounts.refresh');
      } catch (err) {
        eventBus.emit('globalMessage.error', t('Erro ao remover conta'));
      }
    },
  });
};

onMounted(() => {
  eventBus.on('modal.instagram-accounts.open', handleModalOpen);
  eventBus.on('modal.instagram-accounts.close', closeModal);
});

onBeforeUnmount(() => {
  eventBus.off('modal.instagram-accounts.open', handleModalOpen);
  eventBus.off('modal.instagram-accounts.close', closeModal);
});

defineExpose({ openForEdit, closeModal });
</script>
